import React, { useState, useEffect } from 'react';
import {
  Building2, MapPin, CreditCard, User, Users, Briefcase,
  Edit2, Save, X, Loader, AlertCircle, CheckCircle, Plus, ArrowLeft, Trash2,
} from 'lucide-react';
import { establishmentAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import './MyProfile.css';
import './Establishments.css';

// ── Field row (reuses mp-* classes from MyProfile.css) ──────────────
const Field = ({ label, value, name, editing, onChange, type = 'text', readOnly = false }) => (
  <div className="mp-field">
    <span className="mp-field-label">{label}</span>
    {editing && !readOnly ? (
      <div className="mp-field-input-wrap">
        <input
          className="mp-field-input"
          type={type}
          name={name}
          value={value ?? ''}
          onChange={onChange}
        />
      </div>
    ) : (
      <span className="mp-field-value">{value || <span className="mp-field-empty">—</span>}</span>
    )}
  </div>
);

// ── Section card (reuses mp-* classes from MyProfile.css) ────────────
const Section = ({ icon: Icon, title, children }) => (
  <div className="mp-section">
    <div className="mp-section-head">
      <Icon size={16} className="mp-section-icon" />
      <h3 className="mp-section-title">{title}</h3>
    </div>
    <div className="mp-section-body">{children}</div>
  </div>
);

// ── Departments section ───────────────────────────────────────────────
function DeptSection({ departments, setDepartments }) {
  const [input, setInput] = useState('');

  const add = () => {
    const v = input.trim();
    if (v && !departments.includes(v)) {
      setDepartments(prev => [...prev, v]);
      setInput('');
    }
  };

  return (
    <Section icon={Briefcase} title="Departments">
      <div className="est-dept-body">
        <div className="est-dept-chips">
          {departments.length === 0
            ? <span className="est-dept-empty">No departments added yet</span>
            : departments.map(d => (
              <div key={d} className="est-dept-chip">
                <span>{d}</span>
                <button
                  className="est-dept-del"
                  onClick={() => setDepartments(prev => prev.filter(x => x !== d))}
                  type="button"
                >
                  <X size={11} />
                </button>
              </div>
            ))
          }
        </div>
        <div className="est-dept-add-row">
          <div className="mp-field-input-wrap" style={{ flex: 1 }}>
            <input
              className="mp-field-input"
              placeholder="Department name…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            />
          </div>
          <button className="est-dept-add-btn" onClick={add} type="button">
            <Plus size={14} /> Add
          </button>
        </div>
      </div>
    </Section>
  );
}

// ── API mapping helpers ───────────────────────────────────────────────
const mapApiToForm = (est) => ({
  establishmentName: est.name || '',
  code: est.code || '',
  contractStartDate: est.contract_start_date || '',
  contractValidTill: est.contract_valid_till || '',
  gstin: est.gstin || '',
  pan: est.pan || '',
  esicCode: est.esic_code || '',
  epfCode: est.epf_code || '',
  bankName: est.bank_name || '',
  bankBranch: est.bank_branch || '',
  bankAccountNumber: est.bank_account_number || '',
  bankIfsc: est.bank_ifsc || '',
  mainLocation: est.main_location || '',
  mainAddress: est.main_address || '',
  mainTelephone: est.main_telephone || '',
  mainFax: est.main_fax || '',
  mainEmail: est.main_email || '',
  registeredLocation: est.registered_location || '',
  registeredAddress: est.registered_address || '',
  registeredTelephone: est.registered_telephone || '',
  registeredFax: est.registered_fax || '',
  registeredEmail: est.registered_email || '',
  employerName: est.employer_name || '',
  employerDesignation: est.employer_designation || '',
  employerResidentialAddress: est.employer_residential_address || '',
  employerTelephone: est.employer_telephone || '',
  employerMobile: est.employer_mobile || '',
  employerEmail: est.employer_email || '',
  managerName: est.manager_name || '',
  managerDesignation: est.manager_designation || '',
  managerResidentialAddress: est.manager_residential_address || '',
  managerTelephone: est.manager_telephone || '',
  managerMobile: est.manager_mobile || '',
  managerEmail: est.manager_email || '',
  authSignatoryName: est.auth_signatory_name || '',
  authSignatoryDesignation: est.auth_signatory_designation || '',
  authSignatoryResidentialAddress: est.auth_signatory_residential_address || '',
  authSignatoryTelephone: est.auth_signatory_telephone || '',
  authSignatoryMobile: est.auth_signatory_mobile || '',
  authSignatoryEmail: est.auth_signatory_email || '',
});

const mapFormToApi = (form, departments, existingEst) => ({
  name: form.establishmentName,
  code: existingEst?.code || form.code || '',
  branch: form.mainLocation || existingEst?.branch || 'Main',
  address: form.mainAddress || '',
  city: form.mainLocation || existingEst?.city || '',
  state: existingEst?.state || '',
  zipCode: existingEst?.zip_code || existingEst?.zipCode || '',
  contractStartDate: form.contractStartDate,
  contractValidTill: form.contractValidTill,
  departments: JSON.stringify(departments),
  gstin: form.gstin || null,
  pan: form.pan || null,
  esicCode: form.esicCode || null,
  epfCode: form.epfCode || null,
  bankName: form.bankName || null,
  bankBranch: form.bankBranch || null,
  bankAccountNumber: form.bankAccountNumber || null,
  bankIfsc: form.bankIfsc || null,
  mainLocation: form.mainLocation,
  mainAddress: form.mainAddress,
  mainTelephone: form.mainTelephone,
  mainFax: form.mainFax,
  mainEmail: form.mainEmail,
  registeredLocation: form.registeredLocation,
  registeredAddress: form.registeredAddress,
  registeredTelephone: form.registeredTelephone,
  registeredFax: form.registeredFax,
  registeredEmail: form.registeredEmail,
  employerName: form.employerName,
  employerDesignation: form.employerDesignation,
  employerResidentialAddress: form.employerResidentialAddress,
  employerTelephone: form.employerTelephone,
  employerMobile: form.employerMobile,
  employerEmail: form.employerEmail,
  managerName: form.managerName,
  managerDesignation: form.managerDesignation,
  managerResidentialAddress: form.managerResidentialAddress,
  managerTelephone: form.managerTelephone,
  managerMobile: form.managerMobile,
  managerEmail: form.managerEmail,
  authSignatoryName: form.authSignatoryName,
  authSignatoryDesignation: form.authSignatoryDesignation,
  authSignatoryResidentialAddress: form.authSignatoryResidentialAddress,
  authSignatoryTelephone: form.authSignatoryTelephone,
  authSignatoryMobile: form.authSignatoryMobile,
  authSignatoryEmail: form.authSignatoryEmail,
});

const EMPTY_FORM = {
  establishmentName: '', code: '',
  contractStartDate: '', contractValidTill: '',
  gstin: '', pan: '', esicCode: '', epfCode: '',
  bankName: '', bankBranch: '', bankAccountNumber: '', bankIfsc: '',
  mainLocation: '', mainAddress: '', mainTelephone: '', mainFax: '', mainEmail: '',
  registeredLocation: '', registeredAddress: '', registeredTelephone: '', registeredFax: '', registeredEmail: '',
  employerName: '', employerDesignation: '', employerResidentialAddress: '', employerTelephone: '', employerMobile: '', employerEmail: '',
  managerName: '', managerDesignation: '', managerResidentialAddress: '', managerTelephone: '', managerMobile: '', managerEmail: '',
  authSignatoryName: '', authSignatoryDesignation: '', authSignatoryResidentialAddress: '', authSignatoryTelephone: '', authSignatoryMobile: '', authSignatoryEmail: '',
};

// ── Main component ────────────────────────────────────────────────────
export default function Establishments() {
  const [establishments, setEstablishments] = useState([]);
  const [loading, setLoading]               = useState(true);
  // editingEst: null = list view | 'new' = create form | {...} = edit form
  const [editingEst, setEditingEst]         = useState(null);
  const [draft, setDraft]                   = useState(EMPTY_FORM);
  const [departments, setDepartments]       = useState([]);
  const [saving, setSaving]                 = useState(false);
  const [toast, setToast]                   = useState(null);

  const isNew = editingEst === 'new';

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await establishmentAPI.getAll();
      setEstablishments(res.establishments || res || []);
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Could not load organizations' });
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (est) => {
    setEditingEst(est);
    setDraft(mapApiToForm(est));
    let depts = [];
    if (est.departments) {
      try { depts = JSON.parse(est.departments); } catch {}
    }
    setDepartments(depts);
    setToast(null);
  };

  const openNew = () => {
    setEditingEst('new');
    setDraft({ ...EMPTY_FORM });
    setDepartments([]);
    setToast(null);
  };

  const handleCancel = () => { setEditingEst(null); setToast(null); };

  const handleDelete = async (est) => {
    if (!est?.id) return;
    if (!window.confirm(`Delete organization "${est.name}"? This can be reversed by a database admin.`)) return;
    try {
      await establishmentAPI.delete(est.id);
      notifySavedAndReload(`Deleted "${est.name}".`);
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to delete organization.' });
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setDraft(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!draft.establishmentName) {
      setToast({ type: 'error', msg: 'Organization name is required.' });
      return;
    }
    setSaving(true);
    setToast(null);
    try {
      const payload = mapFormToApi(draft, departments, isNew ? null : editingEst);
      if (isNew) {
        await establishmentAPI.create(payload);
      } else {
        await establishmentAPI.update(editingEst.id, payload);
      }
      notifySavedAndReload(isNew ? 'Organization created successfully.' : 'Organization updated successfully.');
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to save changes.' });
    } finally {
      setSaving(false);
    }
  };

  // ── LIST VIEW ─────────────────────────────────────────────────────
  if (!editingEst) {
    return (
      <div className="mp-page">
        <div className="est-toolbar">
          <h1 className="est-page-title">Organizations</h1>
          <button className="mp-btn-edit" onClick={openNew}>
            <Plus size={15} /> New Organization
          </button>
        </div>

        {toast && (
          <div className={`mp-toast ${toast.type}`}>
            {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            <span>{toast.msg}</span>
          </div>
        )}

        {loading ? (
          <div className="mp-state-center">
            <Loader size={28} className="mp-spinner" />
            <p>Loading organizations…</p>
          </div>
        ) : establishments.length === 0 ? (
          <div className="mp-state-center">
            <Building2 size={36} />
            <p>No organizations yet. Create one to get started.</p>
          </div>
        ) : (
          <div className="est-list">
            {establishments.map(est => (
              <div key={est.id} className="est-card">
                <div className="est-card-icon"><Building2 size={22} /></div>
                <div className="est-card-body">
                  <div className="est-card-name">{est.name}</div>
                  <div className="est-card-meta">
                    {est.code && <span>{est.code}</span>}
                    {(est.main_location || est.city) && (
                      <>
                        <span className="est-dot">·</span>
                        <span>{est.main_location || est.city}</span>
                      </>
                    )}
                    {est.gstin && (
                      <>
                        <span className="est-dot">·</span>
                        <span>GSTIN: {est.gstin}</span>
                      </>
                    )}
                    {est.epf_code && (
                      <>
                        <span className="est-dot">·</span>
                        <span>EPF: {est.epf_code}</span>
                      </>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="mp-btn-edit" onClick={() => openEdit(est)}>
                    <Edit2 size={14} /> Edit
                  </button>
                  <button className="mp-btn-cancel" onClick={() => handleDelete(est)} title="Delete organization">
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── EDIT / CREATE VIEW ────────────────────────────────────────────
  return (
    <div className="mp-page">
      {/* Hero banner */}
      <div className="mp-hero">
        <div className="mp-avatar est-avatar">
          <Building2 size={26} />
        </div>
        <div className="mp-hero-info">
          <h1 className="mp-hero-name">
            {isNew ? 'New Organization' : editingEst.name}
          </h1>
          {!isNew && editingEst.code && (
            <div className="mp-hero-id">Code: {editingEst.code}</div>
          )}
          {!isNew && (editingEst.main_location || editingEst.city) && (
            <div className="mp-hero-meta">
              <span>{editingEst.main_location || editingEst.city}</span>
            </div>
          )}
        </div>
        <div className="mp-hero-actions">
          <button className="mp-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? <Loader size={15} className="mp-spinner-sm" /> : <Save size={15} />}
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button className="mp-btn-cancel" onClick={handleCancel} disabled={saving}>
            <ArrowLeft size={15} /> Back
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`mp-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          <span>{toast.msg}</span>
        </div>
      )}

      {/* Detail sections */}
      <div className="mp-grid">

        {/* Basic Info */}
        <Section icon={Building2} title="Basic Info">
          <Field label="Name"           value={draft.establishmentName} name="establishmentName" editing={true} onChange={handleChange} />
          {!isNew && (
            <Field label="Code"          value={editingEst.code}         name="code"              editing={false} />
          )}
          <Field label="Contract Start" value={draft.contractStartDate} name="contractStartDate" editing={true} onChange={handleChange} type="date" />
          <Field label="Contract End"   value={draft.contractValidTill} name="contractValidTill"  editing={true} onChange={handleChange} type="date" />
        </Section>

        {/* Tax & Compliance */}
        <Section icon={CreditCard} title="Tax & Compliance">
          <Field label="GSTIN"     value={draft.gstin}    name="gstin"    editing={true} onChange={handleChange} />
          <Field label="PAN"       value={draft.pan}      name="pan"      editing={true} onChange={handleChange} />
          <Field label="ESIC Code" value={draft.esicCode} name="esicCode" editing={true} onChange={handleChange} />
          <Field label="EPF Code"  value={draft.epfCode}  name="epfCode"  editing={true} onChange={handleChange} />
        </Section>

        {/* Bank Details */}
        <Section icon={Building2} title="Bank Details">
          <Field label="Bank Name"   value={draft.bankName}          name="bankName"          editing={true} onChange={handleChange} />
          <Field label="Branch"      value={draft.bankBranch}        name="bankBranch"        editing={true} onChange={handleChange} />
          <Field label="Account No." value={draft.bankAccountNumber} name="bankAccountNumber" editing={true} onChange={handleChange} />
          <Field label="IFSC"        value={draft.bankIfsc}          name="bankIfsc"          editing={true} onChange={handleChange} />
        </Section>

        {/* Departments */}
        <DeptSection departments={departments} setDepartments={setDepartments} />

        {/* Main Office */}
        <Section icon={MapPin} title="Main Office">
          <Field label="City / Location" value={draft.mainLocation}  name="mainLocation"  editing={true} onChange={handleChange} />
          <Field label="Address"         value={draft.mainAddress}   name="mainAddress"   editing={true} onChange={handleChange} />
          <Field label="Telephone"       value={draft.mainTelephone} name="mainTelephone" editing={true} onChange={handleChange} />
          <Field label="Fax"             value={draft.mainFax}       name="mainFax"       editing={true} onChange={handleChange} />
          <Field label="Email"           value={draft.mainEmail}     name="mainEmail"     editing={true} onChange={handleChange} type="email" />
        </Section>

        {/* Registered Office */}
        <Section icon={MapPin} title="Registered Office">
          <Field label="City / Location" value={draft.registeredLocation}  name="registeredLocation"  editing={true} onChange={handleChange} />
          <Field label="Address"         value={draft.registeredAddress}   name="registeredAddress"   editing={true} onChange={handleChange} />
          <Field label="Telephone"       value={draft.registeredTelephone} name="registeredTelephone" editing={true} onChange={handleChange} />
          <Field label="Fax"             value={draft.registeredFax}       name="registeredFax"       editing={true} onChange={handleChange} />
          <Field label="Email"           value={draft.registeredEmail}     name="registeredEmail"     editing={true} onChange={handleChange} type="email" />
        </Section>

        {/* Employer */}
        <Section icon={User} title="Employer">
          <Field label="Name"        value={draft.employerName}               name="employerName"               editing={true} onChange={handleChange} />
          <Field label="Designation" value={draft.employerDesignation}        name="employerDesignation"        editing={true} onChange={handleChange} />
          <Field label="Address"     value={draft.employerResidentialAddress} name="employerResidentialAddress" editing={true} onChange={handleChange} />
          <Field label="Telephone"   value={draft.employerTelephone}          name="employerTelephone"          editing={true} onChange={handleChange} />
          <Field label="Mobile"      value={draft.employerMobile}             name="employerMobile"             editing={true} onChange={handleChange} />
          <Field label="Email"       value={draft.employerEmail}              name="employerEmail"              editing={true} onChange={handleChange} type="email" />
        </Section>

        {/* Management — Manager + Signatory in one section */}
        <Section icon={Users} title="Management">
          <Field label="Manager Name"        value={draft.managerName}               name="managerName"               editing={true} onChange={handleChange} />
          <Field label="Manager Designation" value={draft.managerDesignation}        name="managerDesignation"        editing={true} onChange={handleChange} />
          <Field label="Manager Address"     value={draft.managerResidentialAddress} name="managerResidentialAddress" editing={true} onChange={handleChange} />
          <Field label="Manager Telephone"   value={draft.managerTelephone}          name="managerTelephone"          editing={true} onChange={handleChange} />
          <Field label="Manager Mobile"      value={draft.managerMobile}             name="managerMobile"             editing={true} onChange={handleChange} />
          <Field label="Manager Email"       value={draft.managerEmail}              name="managerEmail"              editing={true} onChange={handleChange} type="email" />
          <div className="est-section-divider" />
          <Field label="Signatory Name"        value={draft.authSignatoryName}               name="authSignatoryName"               editing={true} onChange={handleChange} />
          <Field label="Signatory Designation" value={draft.authSignatoryDesignation}        name="authSignatoryDesignation"        editing={true} onChange={handleChange} />
          <Field label="Signatory Address"     value={draft.authSignatoryResidentialAddress} name="authSignatoryResidentialAddress" editing={true} onChange={handleChange} />
          <Field label="Signatory Telephone"   value={draft.authSignatoryTelephone}          name="authSignatoryTelephone"          editing={true} onChange={handleChange} />
          <Field label="Signatory Mobile"      value={draft.authSignatoryMobile}             name="authSignatoryMobile"             editing={true} onChange={handleChange} />
          <Field label="Signatory Email"       value={draft.authSignatoryEmail}              name="authSignatoryEmail"              editing={true} onChange={handleChange} type="email" />
        </Section>

      </div>

      {/* Bottom action row — mirrors hero buttons for long forms */}
      <div className="mp-hero-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
        <div>
          {!isNew && (
            <button className="mp-btn-cancel" onClick={() => handleDelete(editingEst)} disabled={saving} title="Delete organization">
              <Trash2 size={15} /> Delete
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="mp-btn-cancel" onClick={handleCancel} disabled={saving}>
            <ArrowLeft size={15} /> Back
          </button>
          <button className="mp-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? <Loader size={15} className="mp-spinner-sm" /> : <Save size={15} />}
            {saving ? 'Saving…' : (isNew ? 'Create Organization' : 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}
